import { NavLink } from 'react-router-dom';
import {
  Archive,
  Boxes,
  Headset,
  HelpCircle,
  IdCard,
  Image,
  LayoutDashboard,
  Megaphone,
  MessagesSquare,
  Package,
  PackageOpen,
  PackageSearch,
  RotateCcw,
  Shield,
  SlidersHorizontal,
  Split,
  Tags,
  UserRound,
  Users,
  Video,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { StaffRole } from '@/shared/lib/auth';
import { env } from '@/shared/lib/env';
import { cn } from '@/shared/utils/cn';
import { useSupportUnreadCount } from '@/entities/support-chat';
import { useSplitsPendingCount } from '@/entities/splits';
import { useReportsPendingCount } from '@/entities/reports';
import { useReturnsPendingCount } from '@/entities/returns';
import { useCurrentUser } from '@/entities/auth';
import { hasPerm } from '@/shared/hooks/usePerm';

type NavItem = {
  to: string;
  i18nKey: string;
  icon: LucideIcon;
  end?: boolean;
  /** Legacy hardcoded-role fallback for envs where `/auth/me` is unavailable. */
  roles: readonly StaffRole[];
  /** Permission path checked first when `useCurrentUser().permissions` is present. */
  perm?: string;
};

const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/dashboard',
    i18nKey: 'nav.dashboard',
    icon: LayoutDashboard,
    roles: ['OWNER'] as const,
    perm: 'dashboard.view',
  },
  {
    to: '/parties',
    i18nKey: 'nav.parties',
    icon: Boxes,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'parties.view',
  },
  {
    to: '/packages',
    i18nKey: 'nav.packages',
    icon: PackageOpen,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'packages.view',
  },
  {
    to: '/splits',
    i18nKey: 'nav.splits',
    icon: Split,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'splits.view',
  },
  {
    to: '/parcels',
    i18nKey: 'nav.parcels',
    icon: Package,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'parcels.view',
  },
  {
    to: '/warehouse',
    i18nKey: 'nav.warehouse',
    icon: PackageSearch,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'warehouse.view',
  },
  {
    to: '/cargo-ids',
    i18nKey: 'nav.cargoIds',
    icon: IdCard,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'cargo_ids.view',
  },
  {
    to: '/clients',
    i18nKey: 'nav.clients',
    icon: UserRound,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'clients.view',
  },
  {
    to: '/legacy-clients',
    i18nKey: 'nav.legacyClients',
    icon: Archive,
    end: true,
    roles: ['OWNER'] as const,
    perm: 'legacy_clients.view',
  },
  {
    to: '/staff',
    i18nKey: 'nav.staff',
    icon: Users,
    roles: ['OWNER'] as const,
    perm: 'staff.view',
  },
  {
    to: '/roles',
    i18nKey: 'nav.roles',
    icon: Shield,
    roles: ['OWNER'] as const,
    perm: 'roles.view',
  },
  {
    to: '/support',
    i18nKey: 'nav.support',
    icon: MessagesSquare,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'support.view',
  },
  {
    to: '/support-staff',
    i18nKey: 'nav.supportStaff',
    icon: Headset,
    roles: ['OWNER'] as const,
    perm: 'support_staff.view',
  },
  {
    to: '/broadcast',
    i18nKey: 'nav.broadcast',
    icon: Megaphone,
    roles: ['OWNER'] as const,
    perm: 'broadcast.view',
  },
  {
    to: '/categories',
    i18nKey: 'nav.categories',
    icon: Tags,
    roles: ['OWNER'] as const,
    perm: 'categories.view',
  },
  {
    to: '/faqs',
    i18nKey: 'nav.faqs',
    icon: HelpCircle,
    roles: ['OWNER'] as const,
    perm: 'faqs.view',
  },
  {
    to: '/pricing',
    i18nKey: 'nav.pricing',
    icon: Wallet,
    roles: ['OWNER'] as const,
    perm: 'pricing.view',
  },
  {
    // Super-only: no `perm`, so Sidebar shows it only to `__super__` (and the
    // legacy OWNER role in the permission-less fallback path).
    to: '/limit-config',
    i18nKey: 'nav.limitConfig',
    icon: SlidersHorizontal,
    roles: ['OWNER'] as const,
  },
  {
    to: '/reports/photo',
    i18nKey: 'nav.reportsPhoto',
    icon: Image,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'reports.view',
  },
  {
    to: '/reports/video',
    i18nKey: 'nav.reportsVideo',
    icon: Video,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'reports.view',
  },
  {
    to: '/reports/proverka',
    i18nKey: 'nav.reportsProverka',
    icon: Wrench,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'reports.view',
  },
  {
    to: '/returns',
    i18nKey: 'nav.returns',
    icon: RotateCcw,
    end: true,
    roles: ['OWNER', 'JAPAN_WORKER', 'UZBEK_WORKER'] as const,
    perm: 'returns.view',
  },
] as const;

type Props = {
  role: StaffRole;
  collapsed?: boolean;
  onItemClick?: () => void;
};

export const Sidebar = ({ role, collapsed = false, onItemClick }: Props) => {
  const { t } = useTranslation();
  const { permissions, role: meRole, user, me } = useCurrentUser();
  // When `/auth/me` returned a permission tree, gate by it. Otherwise fall
  // back to the legacy hardcoded role check so envs without dynamic roles
  // still render the nav.
  const visible = NAV_ITEMS.filter((item) => {
    if (permissions) {
      // Items without an explicit `perm` are always visible to the legacy
      // OWNER role (e.g. `/limit-config`) — keep them shown for super users
      // (which the previous behaviour also did).
      if (!item.perm) {
        return permissions.__super__ === true || item.roles.includes(role);
      }
      return hasPerm(permissions, item.perm);
    }
    return item.roles.includes(role);
  });
  const { data: unread } = useSupportUnreadCount();
  const unreadCount = unread?.count ?? 0;
  const unreadLabel = unreadCount > 99 ? '99+' : String(unreadCount);
  const canViewSplits = hasPerm(permissions, 'splits.view');
  const { data: splitsPendingCount } = useSplitsPendingCount(canViewSplits);
  const pendingSplitsCount = splitsPendingCount ?? 0;
  const pendingSplitsLabel = pendingSplitsCount > 99 ? '99+' : String(pendingSplitsCount);
  const canViewReports = hasPerm(permissions, 'reports.view');
  const { data: reportsPending } = useReportsPendingCount(canViewReports);
  const photoPending = reportsPending?.photo ?? 0;
  const videoPending = reportsPending?.video ?? 0;
  const proverkaPending = reportsPending?.proverka ?? 0;
  const photoLabel = photoPending > 99 ? '99+' : String(photoPending);
  const videoLabel = videoPending > 99 ? '99+' : String(videoPending);
  const proverkaLabel = proverkaPending > 99 ? '99+' : String(proverkaPending);
  const canViewReturns = hasPerm(permissions, 'returns.view');
  const { data: returnsPending } = useReturnsPendingCount(canViewReturns);
  const returnsPendingCount = returnsPending ?? 0;
  const returnsLabel = returnsPendingCount > 99 ? '99+' : String(returnsPendingCount);
  // Footer subtitle: super staff get a translated label; custom roles render
  // their literal name verbatim (no `t()` lookup — owners enter free-text role
  // names that have no i18n key, so going through `t('roles.<name>')` would
  // surface raw keys like "roles.test2"). Empty when there is no role info.
  let roleLabel: string | null = null;
  if (meRole?.is_super === true) {
    roleLabel = t('header.superLabel');
  } else if (meRole?.name) {
    roleLabel = meRole.name;
  }
  const userLabel =
    (me?.full_name ?? user?.full_name)?.trim() ||
    me?.username ||
    user?.username ||
    t('header.user');
  return (
    <aside
      aria-label="Asosiy navigatsiya"
      className={cn(
        'flex h-full flex-col bg-sidebar text-white',
        collapsed ? 'w-16' : 'w-64',
        'transition-[width] duration-200',
      )}
    >
      <div
        className={cn(
          'flex h-16 items-center gap-3 border-b border-white/10 px-4',
          collapsed && 'justify-center px-2',
        )}
      >
        <img
          src="/logo.jpg"
          alt="Tez Cargo"
          className="h-10 w-10 shrink-0 rounded-lg bg-white object-contain p-1 shadow"
        />
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{env.appName}</p>
            <p className="text-xs text-white/60">Admin panel</p>
          </div>
        )}
      </div>
      <nav className="sidebar-scroll flex-1 overflow-y-auto px-2 py-3">
        <ul className="flex flex-col gap-1">
          {visible.map(({ to, i18nKey, icon: Icon, end }) => {
            const isSupport = to === '/support' && unreadCount > 0;
            const isSplits = to === '/splits' && pendingSplitsCount > 0;
            const isReportsPhoto = to === '/reports/photo' && photoPending > 0;
            const isReportsVideo = to === '/reports/video' && videoPending > 0;
            const isReportsProverka = to === '/reports/proverka' && proverkaPending > 0;
            const isReturns = to === '/returns' && returnsPendingCount > 0;
            const showBadge =
              isSupport ||
              isSplits ||
              isReportsPhoto ||
              isReportsVideo ||
              isReportsProverka ||
              isReturns;
            const badgeLabel = isSupport
              ? unreadLabel
              : isSplits
                ? pendingSplitsLabel
                : isReportsPhoto
                  ? photoLabel
                  : isReportsVideo
                    ? videoLabel
                    : isReportsProverka
                      ? proverkaLabel
                      : returnsLabel;
            const badgeTitle = isSupport
              ? t('nav.unreadTooltip', { count: unreadCount })
              : isSplits
                ? t('nav.splitsPendingTooltip', { count: pendingSplitsCount })
                : isReportsPhoto
                  ? t('nav.reportsPhotoPendingTooltip', { count: photoPending })
                  : isReportsVideo
                    ? t('nav.reportsVideoPendingTooltip', { count: videoPending })
                    : isReportsProverka
                      ? t('nav.reportsProverkaPendingTooltip', { count: proverkaPending })
                      : t('nav.returnsPendingTooltip', { count: returnsPendingCount });
            return (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  onClick={onItemClick}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive ? 'bg-brand-600 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white',
                      collapsed && 'justify-center px-2',
                    )
                  }
                >
                  <span className="relative shrink-0">
                    <Icon className="h-5 w-5" aria-hidden />
                    {showBadge && collapsed && (
                      <span
                        title={badgeTitle}
                        className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-sidebar"
                      >
                        {badgeLabel}
                      </span>
                    )}
                  </span>
                  {!collapsed && <span className="truncate">{t(i18nKey)}</span>}
                  {showBadge && !collapsed && (
                    <span
                      title={badgeTitle}
                      className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold leading-none text-white"
                    >
                      {badgeLabel}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
      {!collapsed && (
        <div className="border-t border-white/10 px-4 py-3 text-xs text-white/60">
          <p className="truncate font-semibold text-white/80">{userLabel}</p>
          {roleLabel && <p className="truncate">{roleLabel}</p>}
        </div>
      )}
    </aside>
  );
};
